import { useEffect, useId, useState } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import {
  ArrowDown,
  ArrowUp,
  Braces,
  Calendar,
  Hash,
  Plus,
  Shuffle,
  Trash2,
  Type,
} from 'lucide-react';
import type { TaxonomyField, TrackingCodePart, TrackingPartKind } from '@/entities/taxonomy';
import { FormSheet } from '@/shared/ui/FormSheet';
import { patternToString } from '../../lib/trackingPattern';

export interface TrackingCodeComposerProps {
  open: boolean;
  field: TaxonomyField;
  /** All schema fields — the pattern can reference any non-computed field. */
  fields: TaxonomyField[];
  /** Dialog title override (e.g. "Configure tracking code"). */
  title?: string;
  /** Hide the Name input — for the schema-level tracking code (it has no name). */
  nameless?: boolean;
  /**
   * Codes this SCHEMA has issued — every computed column's, not this one's.
   *
   * The risk being warned about is per column, since a code is looked up by
   * `(taxonomy, field, context)`. A schema-wide number cannot tell whether any of
   * them came from the pattern being edited, so the copy says "if any came from
   * this pattern" rather than asserting a consequence that may not apply. Making
   * it exact would mean a per-column count on the detail response; the honest
   * sentence costs nothing and does not.
   */
  mintedCount?: number;
  onClose: () => void;
  onSave: (patch: { name: string; pattern: TrackingCodePart[] }) => void;
}

// Getters, not values: a resolved label would freeze at import.
const KIND_META: Record<TrackingPartKind, { label: string; icon: typeof Hash }> = {
  field: {
    get label() {
      return i18n.t('knowledge:taxonomy.trackingCodeComposer.field');
    },
    icon: Braces,
  },
  literal: {
    get label() {
      return i18n.t('knowledge:taxonomy.trackingCodeComposer.text');
    },
    icon: Type,
  },
  date: {
    get label() {
      return i18n.t('knowledge:taxonomy.trackingCodeComposer.date');
    },
    icon: Calendar,
  },
  sequence: {
    get label() {
      return i18n.t('knowledge:taxonomy.trackingCodeComposer.sequence');
    },
    icon: Hash,
  },
  random: {
    get label() {
      return i18n.t('knowledge:taxonomy.trackingCodeComposer.random');
    },
    icon: Shuffle,
  },
};

export function TrackingCodeComposer(props: TrackingCodeComposerProps) {
  const { t } = useTranslation('knowledge');
  const {
    open,
    field,
    fields,
    title = t('taxonomy.trackingCodeComposer.configureVariable'),
    nameless,
    onClose,
    onSave,
    mintedCount,
  } = props;
  const nameId = useId();
  const [name, setName] = useState(field.name);
  const [parts, setParts] = useState<TrackingCodePart[]>(field.pattern ?? []);

  // Keyed on `field.id`, not `field`: both callers rebuild the prop as the
  // parent renders — SchemaSettings passes an object literal — so depending on
  // its identity re-seeded `parts` from the PERSISTED pattern mid-edit and
  // silently discarded whatever the user had just added or reordered. An
  // autosave landing while the dialog is open is enough to trigger it.
  useEffect(() => {
    if (open) {
      setName(field.name);
      setParts(field.pattern ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed on open or a genuinely different field, never on the parent re-rendering
  }, [open, field.id]);

  // Referenceable fields: anything with values/output, not this computed field.
  const refFields = fields.filter((f) => f.type !== 'computed' && f.id !== field.id);

  const patch = (i: number, next: Partial<TrackingCodePart>) =>
    setParts((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...next } : p)));

  const remove = (i: number) => setParts((prev) => prev.filter((_, idx) => idx !== i));

  const move = (i: number, dir: -1 | 1) =>
    setParts((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const add = (kind: TrackingPartKind) => {
    const defaults: Record<TrackingPartKind, TrackingCodePart> = {
      field: { kind: 'field', fieldId: refFields[0]?.id ?? null, source: 'code' },
      literal: { kind: 'literal', text: '-' },
      date: { kind: 'date', format: 'YYYY' },
      sequence: { kind: 'sequence', length: 5, charset: 'alphanumeric', mode: 'counter' },
      random: { kind: 'random', length: 6, charset: 'alphanumeric' },
    };
    setParts((prev) => [...prev, defaults[kind]]);
  };

  const preview = patternToString(parts, fields);
  const canSave = nameless || name.trim().length > 0;

  return (
    <FormSheet
      className="w-[44rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={title}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {/* Shown where the edit happens rather than on the page behind it: this
            dialog is the only way either pattern is changed, so one warning here
            covers the schema tracking code and a computed column alike. */}
        {mintedCount !== undefined && mintedCount > 0 && (
          <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
            {t('taxonomy.trackingCodeComposer.editingAffectsMintedCodes', { count: mintedCount })}
          </p>
        )}
        {!nameless && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={nameId}>
              {t('taxonomy.trackingCodeComposer.name')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id={nameId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('taxonomy.trackingCodeComposer.eGAssetTrackingId')}
              maxLength={100}
              className="bg-input"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
              autoFocus
            />
          </div>
        )}

        {/* Live pattern preview */}
        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.trackingCodeComposer.pattern')}</Label>
          <div className="rounded-md border border-border-light bg-surface/50 px-3 py-2 font-mono text-sm text-foreground">
            {preview || (
              <span className="text-muted-foreground">
                {t('taxonomy.trackingCodeComposer.addPartsBuildCode')}
              </span>
            )}
          </div>
        </div>

        {/* Parts list */}
        <div className="flex flex-col gap-2">
          {parts.map((part, i) => {
            const Meta = KIND_META[part.kind];
            return (
              <div
                key={i}
                className="flex items-start gap-2 rounded-md border border-border-light p-2"
              >
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label={t('taxonomy.trackingCodeComposer.moveUp')}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={t('taxonomy.trackingCodeComposer.moveDown')}
                    disabled={i === parts.length - 1}
                    onClick={() => move(i, 1)}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="size-3.5" />
                  </button>
                </div>

                <span className="mt-1 flex w-20 shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Meta.icon className="size-3.5" />
                  {Meta.label}
                </span>

                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  {part.kind === 'field' && (
                    <>
                      <Select
                        value={part.fieldId ?? ''}
                        onValueChange={(v) => patch(i, { fieldId: v })}
                      >
                        <SelectTrigger className="h-8 w-44">
                          <SelectValue
                            placeholder={t('taxonomy.trackingCodeComposer.selectField')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {refFields.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        // eslint-disable-next-line i18next/no-literal-string -- pattern-part source enum
                        value={part.source ?? 'code'}
                        onValueChange={(v) => patch(i, { source: v as 'code' | 'value' })}
                      >
                        <SelectTrigger className="h-8 w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* eslint-disable-next-line i18next/no-literal-string -- source enum */}
                          <SelectItem value="code">
                            {t('taxonomy.trackingCodeComposer.code')}
                          </SelectItem>
                          {/* eslint-disable-next-line i18next/no-literal-string -- source enum */}
                          <SelectItem value="value">
                            {t('taxonomy.trackingCodeComposer.value')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {part.kind === 'literal' && (
                    <Input
                      value={part.text ?? ''}
                      onChange={(e) => patch(i, { text: e.target.value })}
                      placeholder={t('taxonomy.trackingCodeComposer.eG')}
                      maxLength={20}
                      className="h-8 w-40 bg-input"
                    />
                  )}
                  {part.kind === 'date' && (
                    <Input
                      value={part.format ?? ''}
                      // Trimmed to null, matching what `EditFieldModal` does for a
                      // date FIELD. Clearing the box means "no mask", and sending
                      // `''` made the autosave 400.
                      onChange={(e) => patch(i, { format: e.target.value.trim() || null })}
                      placeholder="YYYY"
                      maxLength={12}
                      className="h-8 w-32 bg-input font-mono"
                    />
                  )}
                  {part.kind === 'sequence' && (
                    <>
                      <Input
                        type="number"
                        min={1}
                        value={part.length ?? 5}
                        onChange={(e) => patch(i, { length: Number(e.target.value) || 1 })}
                        className="h-8 w-16 bg-input"
                        aria-label={t('taxonomy.trackingCodeComposer.length')}
                      />
                      <Select
                        // eslint-disable-next-line i18next/no-literal-string -- charset enum
                        value={part.charset ?? 'alphanumeric'}
                        onValueChange={(v) =>
                          patch(i, { charset: v as 'numeric' | 'alphanumeric' })
                        }
                      >
                        <SelectTrigger className="h-8 w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* eslint-disable-next-line i18next/no-literal-string -- charset enum */}
                          <SelectItem value="numeric">
                            {t('taxonomy.trackingCodeComposer.numeric')}
                          </SelectItem>
                          {/* eslint-disable-next-line i18next/no-literal-string -- charset enum */}
                          <SelectItem value="alphanumeric">
                            {t('taxonomy.trackingCodeComposer.alphanumeric')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {part.kind === 'random' && (
                    <>
                      <Input
                        type="number"
                        min={1}
                        value={part.length ?? 6}
                        onChange={(e) => patch(i, { length: Number(e.target.value) || 1 })}
                        className="h-8 w-16 bg-input"
                        aria-label={t('taxonomy.trackingCodeComposer.length')}
                      />
                      <Select
                        // eslint-disable-next-line i18next/no-literal-string -- charset enum
                        value={part.charset ?? 'alphanumeric'}
                        onValueChange={(v) =>
                          patch(i, { charset: v as 'numeric' | 'alphanumeric' })
                        }
                      >
                        <SelectTrigger className="h-8 w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* eslint-disable-next-line i18next/no-literal-string -- charset enum */}
                          <SelectItem value="numeric">
                            {t('taxonomy.trackingCodeComposer.numeric')}
                          </SelectItem>
                          {/* eslint-disable-next-line i18next/no-literal-string -- charset enum */}
                          <SelectItem value="alphanumeric">
                            {t('taxonomy.trackingCodeComposer.alphanumeric')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-[11px] text-muted-foreground">
                        {t('taxonomy.trackingCodeComposer.randomChars')}
                      </span>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  aria-label={t('taxonomy.trackingCodeComposer.removePart')}
                  onClick={() => remove(i)}
                  className="mt-1 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            );
          })}

          <div className="flex flex-wrap gap-2">
            {(Object.keys(KIND_META) as TrackingPartKind[]).map((kind) => {
              const Meta = KIND_META[kind];
              return (
                <Button key={kind} variant="outline" size="mini" onClick={() => add(kind)}>
                  <Plus className="size-3.5" />
                  <Meta.icon className="size-3.5" />
                  {Meta.label}
                </Button>
              );
            })}
          </div>
          {!parts.some((p) => p.kind === 'sequence' || p.kind === 'random') && parts.length > 0 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              {t('taxonomy.trackingCodeComposer.addSequenceRandomPartKeep')}
            </p>
          )}
        </div>
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: t('taxonomy.trackingCodeComposer.savePattern'),
          onClick: () => canSave && onSave({ name: name.trim(), pattern: parts }),
          disabled: !canSave,
        }}
        secondary={{ label: t('taxonomy.trackingCodeComposer.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

TrackingCodeComposer.displayName = 'TrackingCodeComposer';
